/**
 * studentAssessmentsViewModel — `(reduxState) → props` de « Mes contrôles » (patron A).
 *
 * Ce que lit `SchoolAssessmentsScreen.js` pour l'élève connecté (ni `studentId` ni `student` en
 * paramètre : ceux-là sont le contexte du tuteur, qui ne voit jamais l'espace élève) :
 * - la liste = `schoolAssessments.byStudentId[user.id]`, rangée là par le réducteur à la réponse de
 *   `getSchoolAssessments { student_id, upcoming }`, et tenue à jour par lui à chaque ajout,
 *   modification ou suppression ;
 * - le premier chargement = rien encore pour cet élève (`hasDataForStudent`), ici sans attendre que
 *   la demande soit partie : le premier rendu montre déjà la forme de la liste, pas une page vide ;
 * - une relecture avec la liste déjà là fait tourner la flèche de l'en-tête (le tirer-pour-rafraîchir
 *   d'avant). Son échec laisse la liste en place, comme avant ; l'erreur ne s'affiche que sans liste.
 *
 * La feuille d'ajout lit aussi ce que le compte porte : la classe (les chapitres du catalogue en
 * dépendent) et les objectifs pédagogiques (les matières proposées).
 */
import { createSelector } from "reselect"

import {
    chaptersForSubject,
    subjectsForAssessment,
    toStudentAssessmentEdits,
    toStudentAssessments,
    type StudentAssessmentEdit,
} from "../../../adapters/student/Assessments/studentAssessmentsAdapter"
import { type UISchoolAssessment, type UISectionedChapter, type UISubject } from "../../../ui/types"
import { selectDisplayedStudent } from "../../shared/displayedStudent"

export type StudentAssessmentsViewModel = {
    /** L'élève connecté — `null` tant que le compte n'est pas là. */
    studentId: string | number | null
    /** Sa classe : les chapitres du catalogue sont lus pour elle. */
    studentClass: string | null
    /** Les contrôles à venir, par date croissante (l'ordre servi). */
    assessments: UISchoolAssessment[]
    /** Le brouillon et les chapitres de chaque contrôle, pour le rouvrir en modification. */
    edits: Record<string, StudentAssessmentEdit>
    /** Les matières proposées par le formulaire. */
    subjects: UISubject[]
    /** Rien encore pour cet élève, et pas d'erreur : la forme de la liste. */
    loading: boolean
    /** Une relecture est en cours, la liste déjà là. */
    refreshing: boolean
    /** L'échec de la première lecture — jamais par-dessus une liste. */
    error: unknown | null
}

/** Ce que la projection lit : l'élève, sa classe et ses objectifs, la liste rangée, les matières, la tranche. */
export type StudentAssessmentsInputs = {
    studentId: string | number | null | undefined
    studentClass: string | null | undefined
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    objectives: any
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    byStudentId: any
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    subjects: any
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    req: any
}

/**
 * La projection de « Mes contrôles », pure : aussi celle de l'onglet Contrôles de la fiche du tuteur, pour
 * l'élève qu'il regarde.
 */
export function projectStudentAssessments(input: StudentAssessmentsInputs): StudentAssessmentsViewModel {
    const { studentId, studentClass, objectives, byStudentId, subjects, req } = input
    const rows = studentId != null ? byStudentId?.[studentId] : undefined
    const hasRows = rows !== undefined
    const error = !hasRows ? (req?.error ?? null) : null
    return {
        studentId: studentId ?? null,
        studentClass: studentClass ?? null,
        assessments: toStudentAssessments(rows),
        edits: toStudentAssessmentEdits(rows),
        subjects: subjectsForAssessment(subjects, objectives),
        // Rien encore pour cet élève : la forme de la liste, jamais « Aucun contrôle » pendant la lecture.
        loading: studentId != null && !hasRows && error == null,
        refreshing: hasRows && !!req?.request,
        error,
    }
}

/** Redux root is still untyped in this app — keep the VM boundary explicit. */
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
type ReduxState = any

/**
 * Les entrées sont les champs lus, pas le compte entier : chaque geste du jeu recopie
 * `currentUser.object`, et ne doit pas rejouer les adaptateurs.
 */
export const studentAssessmentsViewModel = createSelector(
    [
        (state: ReduxState) => selectDisplayedStudent(state)?.id as string | number | undefined,
        (state: ReduxState) => selectDisplayedStudent(state)?.profile?.class as string | undefined,
        (state: ReduxState) => selectDisplayedStudent(state)?.pedagogical_objectives,
        (state: ReduxState) => state?.data?.schoolAssessments?.byStudentId,
        (state: ReduxState) => state?.data?.staticData?.school_subjects,
        (state: ReduxState) => state?.api?.getSchoolAssessments,
    ],
    (studentId, studentClass, objectives, byStudentId, subjects, req): StudentAssessmentsViewModel =>
        projectStudentAssessments({ studentId, studentClass, objectives, byStudentId, subjects, req })
)

export type StudentAssessmentChaptersViewModel = {
    /** Le catalogue de la matière choisie. */
    chapters: UISectionedChapter[]
    chaptersLoading: boolean
}

/** Les chapitres du catalogue pour la matière du brouillon (`chaptersList`, partagé avec d'autres surfaces). */
export const studentAssessmentChaptersViewModel = createSelector(
    [
        (state: ReduxState) => state?.data?.chapters?.chaptersList,
        (state: ReduxState) => !!state?.api?.getChaptersBySchoolSubjectId?.request,
        (_state: ReduxState, subjectId: string | number | null) => subjectId,
    ],
    (
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        catalog: any,
        chaptersLoading,
        subjectId
    ): StudentAssessmentChaptersViewModel => ({
        chapters: chaptersForSubject(catalog, subjectId),
        chaptersLoading,
    })
)
